import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UsersIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  ChartPieIcon,
  PuzzlePieceIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import {
  PageShell,
  PremiumHero,
  PremiumSection,
  SectionHeader,
  FeatureGrid,
  GlassCard,
  PremiumCTA,
  inputClass,
  btnPrimaryClass,
} from '../premium/PremiumPageUI';

const platformFeatures = [
  { title: 'CRM Suite', description: 'Centralize customer data, track pipelines, and automate follow-ups across every touchpoint.', icon: UsersIcon },
  { title: 'Project Management', description: 'Plan, assign, and monitor work with Gantt views, time tracking, and real-time collaboration.', icon: ClipboardDocumentListIcon },
  { title: 'Financial Management', description: 'Invoicing, expenses, budgeting, and multi-currency reporting in one unified ledger.', icon: CurrencyDollarIcon },
  { title: 'Business Intelligence', description: 'Executive dashboards, KPI tracking, and predictive analytics for data-driven decisions.', icon: ChartPieIcon },
];

const pricingPlans = [
  { name: 'Starter', price: 49, description: 'For small teams getting started', features: ['CRM Basic', '5 Projects', 'Financial Basics', '5GB Storage', 'Up to 3 Users'], recommended: false },
  { name: 'Professional', price: 99, description: 'For growing businesses', features: ['CRM Advanced', 'Unlimited Projects', 'HR Basics', 'BI Dashboards', '25GB · 10 Users'], recommended: true },
  { name: 'Enterprise', price: 249, description: 'For established organizations', features: ['Full CRM Suite', 'Enterprise Finance', 'Advanced BI', 'E-commerce', 'Dedicated Support', 'Unlimited Users'], recommended: false },
];

const integrations = ['Salesforce', 'Microsoft', 'Google', 'Slack', 'Shopify', 'Stripe', 'QuickBooks', 'Zoom'];

export default function BusinessPlatform() {
  const [expandedFeature, setExpandedFeature] = useState(0);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', company: '', message: '' });

  const handleDemo = (e) => {
    e.preventDefault();
    alert('Thank you! Our team will contact you shortly.');
    setDemoForm({ name: '', email: '', company: '', message: '' });
  };

  return (
    <PageShell>
      <PremiumHero
        badge="Business Platform"
        title="Unified"
        highlight="Enterprise OS"
        subtitle="One intelligent platform for CRM, projects, finance, and analytics—designed for scale, security, and seamless integrations."
        stats={[
          { value: '4', label: 'Core Modules' },
          { value: '50+', label: 'Integrations' },
          { value: '99.9%', label: 'Uptime' },
          { value: 'SOC2', label: 'Ready' },
        ]}
        actions={[
          { label: 'Request Demo', primary: true, onClick: () => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' }) },
          { label: 'View Pricing', onClick: () => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }) },
        ]}
      />

      <PremiumSection>
        <SectionHeader badge="Platform" title="All-in-One" highlight="Modules" />
        <FeatureGrid items={platformFeatures} columns={4} />
        <GlassCard className="mt-8 p-8" hover={false}>
          <div className="flex flex-wrap gap-2 mb-4">
            {platformFeatures.map((f, i) => (
              <button key={f.title} type="button" onClick={() => setExpandedFeature(i)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${expandedFeature === i ? 'bg-[#2563EB] text-white' : 'hero-glass-tag'}`}>
                {f.title}
                      </button>
            ))}
          </div>
          <p className="text-slate-600 dark:text-gray-300 leading-relaxed">
            {platformFeatures[expandedFeature].description} Built with enterprise-grade security, role-based access, and API-first architecture for custom workflows.
          </p>
        </GlassCard>
      </PremiumSection>

      <PremiumSection id="pricing" alt>
        <SectionHeader badge="Plans" title="Transparent" highlight="Pricing" />
        <div className="grid md:grid-cols-3 gap-6">
          {pricingPlans.map((plan, i) => (
            <motion.div key={plan.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} whileHover={{ y: -8 }}>
              <GlassCard className={`p-8 h-full relative ${plan.recommended ? 'ring-2 ring-[#2563EB] premium-border-glow' : ''}`}>
                {plan.recommended && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 text-xs font-bold uppercase bg-gradient-to-r from-[#2563EB] to-cyan-500 text-white rounded-full">
                    Most Popular
                  </span>
                )}
                <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                <p className="text-sm text-slate-500 mb-4">{plan.description}</p>
                <p className="text-4xl font-bold mb-6">
                  <span className="bg-gradient-to-r from-[#2563EB] to-cyan-500 bg-clip-text text-transparent">${plan.price}</span>
                  <span className="text-sm text-slate-500 font-normal">/mo</span>
                </p>
                  <ul className="space-y-3 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-600 dark:text-gray-300">
                      <CheckIcon className="w-5 h-5 text-[#2563EB]" />{f}
                      </li>
                    ))}
                  </ul>
                <Link to="/contact" className={`${btnPrimaryClass} w-full justify-center ${plan.recommended ? '' : 'opacity-90'}`}>
                  {plan.name === 'Enterprise' ? 'Contact Sales' : 'Start Free Trial'}
                </Link>
              </GlassCard>
              </motion.div>
            ))}
          </div>
      </PremiumSection>

      <PremiumSection>
        <SectionHeader badge="Ecosystem" title="Seamless" highlight="Integrations" />
        <div className="flex flex-wrap justify-center gap-4">
          {integrations.map((name) => (
            <div key={name} className="px-6 py-4 rounded-2xl hero-glass-tag font-semibold text-sm text-slate-700 dark:text-gray-200 hover:hero-glow transition-all flex items-center gap-2">
              <PuzzlePieceIcon className="w-5 h-5 text-[#2563EB]" />
              {name}
          </div>
          ))}
        </div>
      </PremiumSection>

      <PremiumSection id="demo" alt>
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <SectionHeader badge="Demo" title="See It" highlight="In Action" className="text-left mb-6" />
            <p className="text-slate-600 dark:text-gray-300 leading-relaxed">
              Schedule a personalized walkthrough with our product team and discover how the platform adapts to your workflows.
            </p>
          </div>
          <GlassCard className="p-8" hover={false}>
            <form onSubmit={handleDemo} className="space-y-4">
              <input type="text" placeholder="Full Name" required value={demoForm.name} onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })} className={inputClass} />
              <input type="email" placeholder="Work Email" required value={demoForm.email} onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })} className={inputClass} />
              <input type="text" placeholder="Company" required value={demoForm.company} onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })} className={inputClass} />
              <textarea rows={3} placeholder="Tell us about your needs..." value={demoForm.message} onChange={(e) => setDemoForm({ ...demoForm, message: e.target.value })} className={inputClass} />
              <button type="submit" className={`${btnPrimaryClass} w-full`}>Request Demo</button>
            </form>
          </GlassCard>
        </div>
      </PremiumSection>

      <PremiumCTA
        title="Transform How You Run Your Business"
        subtitle="Deploy a unified platform that scales with your ambitions—from startup to enterprise."
        primary={{ label: 'Get Started', href: '/contact' }}
        secondary={{ label: 'Training Programs', href: '/training-consulting' }}
      />
    </PageShell>
  );
}
